<template>
  <div class="search" @click="showSearch = !showSearch">
    <slot />
  </div>
  <div ref="searchBox" v-if="showSearch" class="search search-box">
    <div class="card no-hover bg-blurry">
      <div class="card-header border-none pt-4 text-center">
        <h2 class="h2 mb-3">{{ isDutch ? "Vluchtzoeker" : "Flight Finder" }}</h2>
        <p class="mb-0">{{ isDutch ? "Voer je vertrek- en bestemmingspunten in, en vind direct de beste beschikbare deals." : "Discover your perfect flight here. Simply input your departure and destination points, and instantly find the best deals available." }}</p>
      </div>
      <div class="card-body">
        <form autocomplete="off">
          <div class="input-group position-relative mb-2">
            <div class="input-group-text">
              <i class="fa-duotone fa-solid fa-plane-departure"></i>
            </div>
            <input type="search" class="form-control form-control-lg" autocomplete="false" id="origin" :placeholder="isDutch ? 'Vertrek vanaf (bijv. Amsterdam)' : 'Departing from (e.g. Amsterdam)'" data-1p-ignore v-model="startAirport.name">
            <div class="search-results" v-if="startAirport.results.length > 0">
              <p class="text-uppercase small fw-bold text-primary mb-2">{{ isDutch ? "Selecteer vliegveld" : "Select your airport" }}</p>
              <div v-for="airport in startAirport.results.slice(0,4)" @click="startAirport.code = airport.code; startAirport.name = airport.name + ' (' + airport.code + ')'; startAirport.results = {}" class="result py-2">
                <span>
                  <NuxtImg
                      class="me-1"
                      width="15"
                      height="11"
                      loading="lazy"
                      :alt="airport.city.country.code + ' flag emoji'"
                      :src="'/flagcdn/16x12/' + airport.city.country.code.toLowerCase() + '.png'"
                  />
                  {{ airport.name }} <span class="small opacity-75">({{ airport.code }})</span></span>
              </div>
            </div>
          </div>
          <div class="input-group position-relative">
            <div class="input-group-text">
              <i class="fa-duotone fa-solid fa-plane-arrival"></i>
            </div>
            <input type="search" class="form-control form-control-lg" autocomplete="false" id="destination" :placeholder="isDutch ? 'Bestemming (bijv. New York)' : 'Arriving at (e.g. New York)'" data-1p-ignore v-model="endAirport.name">
            <div class="search-results" v-if="endAirport.results.length > 0">
              <p class="text-uppercase small fw-bold text-primary mb-2">{{ isDutch ? "Selecteer vliegveld" : "Select your airport" }}</p>
              <div v-for="airport in endAirport.results.slice(0,4)" @click="endAirport.code = airport.code; endAirport.name = airport.name + ' (' + airport.code + ')'; endAirport.results = {}" class="result py-2">
                <span><NuxtImg
                    class="me-1"
                    width="15"
                    height="11"
                    loading="lazy"
                    :alt="airport.city.country.code + ' flag emoji'"
                    :src="'/flagcdn/16x12/' + airport.city.country.code.toLowerCase() + '.png'"
                />{{ airport.name }} <span class="small opacity-75">({{ airport.code }})</span></span>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="card-footer text-center pb-4">
        <button
            v-if="isReady"
            @click="navigate"
            class="btn btn-primary btn-sm fw-bold w-100 text-white">
          {{ (isDutch ? 'Vliegtickets zoeken' : 'Find your ticket')}}
        </button>
        <button v-else
                disabled
                class="btn btn-primary btn-sm fw-bold w-100 text-white">
          {{ (isDutch ? 'Kies eerst je bestemming' : 'Select your airports') }}
        </button>
        <div v-if="isError" class="alert alert-danger mb-0 mt-2" role="alert">
          <p class="mb-0 small" v-if="isDutch">
            Er zijn geen Promo Rewards beschikbaar voor deze combinatie. Probeer andere vertrek- en/of bestemmingspunten.
          </p>
          <p class="mb-0 small" v-else>There are no Promo Rewards available for this combination. Try other departure and/or destination points.</p>
        </div>
      </div>
    </div>
    <div class="overlay" @click="showSearch = false"></div>
  </div>
</template>

<script setup lang="ts">
const { proxy } = useScriptGoogleAnalytics()

const startAirport = ref({
  name: '',
  code: '',
  results: {}
});
const endAirport = ref({
  name: '',
  code: '',
  results: {}
});

const searchBox = ref()
const showSearch = ref(false);
const isError = ref(false);

const isReady = computed(() => {
  return startAirport.value.code && endAirport.value.code
})

const isDutch = inject('isDutch');

const fetchData = async (value: string, target: any) => {
  try {
    const response = await fetch('https://api.skypicker.com/locations/?locale=' + (isDutch.value ? 'nl-NL' : 'en-EN') + '&location_types=airport&term=' + value); // Replace with your API URL
    const data = await response.json();
    target.value.results = data.locations;
  } catch (error) {
    console.error('There was a problem with the fetch operation: ' + error.message);
  }
};

watch(
    startAirport,
    (value) => {
      if (value.name.length >= 3) {
        fetchData(value.name, startAirport);
      }
    },
    { deep: true },
);

watch(
    endAirport,
    (value) => {
      if (value.name.length >= 3) {
        fetchData(value.name, endAirport);
      }
    },
    { deep: true },
);

let navigate = async () => {
  const router = useRouter();
  const runtimeConfig = useRuntimeConfig()
  try {
    const response = await $fetch(runtimeConfig.public.backendUrl +  "/api/v2/airports/multiple", {
      params: {
        'airports[]': [startAirport.value.code.toLowerCase(), endAirport.value.code.toLowerCase()]
      }
    });

    if (response.airports.length !== 2) {
      isError.value = true;
      return;
    }

    await router.push({
      name: isDutch ? 'vluchten-tussen' : 'flights-between',
      params: {
        name: startAirport.value.code.toLowerCase(),
        iata: startAirport.value.code.toLowerCase(),
        toname: startAirport.value.code.toLowerCase(),
        toiata: endAirport.value.code.toLowerCase(),
      }
    });

    await proxy.gtag("event", "search", {
      search_term: `${origin.iata} ${destination.iata}`
    });

    showSearch.value = false;
  } catch (error) {
    isError.value = true;
    endAirport.value.code = '';
    endAirport.value.name = '';
  }
}



</script>